import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground @media(hover:hover){hover:bg-primary/90}",
        destructive: "bg-destructive text-destructive-foreground @media(hover:hover){hover:bg-destructive/90}",
        outline: "border border-input bg-background @media(hover:hover){hover:bg-accent hover:text-accent-foreground}",
        secondary: "bg-secondary text-secondary-foreground @media(hover:hover){hover:bg-secondary/80}",
        ghost: "@media(hover:hover){hover:bg-accent hover:text-accent-foreground}",
        link: "text-primary underline-offset-4 @media(hover:hover){hover:underline}",
        hero: "bg-accent text-black green-glow transition-all duration-500 font-bold tracking-wide @media(hover:hover){hover:bg-accent/90 hover:scale-[1.02]}",
        "hero-outline": "bg-transparent text-foreground border-2 border-accent/30 transition-all duration-500 font-light @media(hover:hover){hover:border-accent hover:bg-accent/5}",
      },
      size: {
        default: "h-auto min-h-[2.5rem] px-4 py-2 text-sm",
        sm: "h-auto min-h-[2.25rem] rounded-md px-3 py-1.5 text-xs",
        lg: "h-auto min-h-[2.75rem] rounded-md px-6 md:px-8 py-2.5 text-sm md:text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
